import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Tooltip,
  UncontrolledTooltip,
  UncontrolledDropdown,
} from "reactstrap";
import ReactDOMServer from "react-dom/server";

import { SCOPE_OF_WORK } from "../../../constants/editor";
import { Icon } from "../../ui/icon";
import { Input } from "../../ui/input";
import Modal from "../../ui/modal";

import global from "../../../scss/dhp.scss";
import { widgetConfig } from "../editor_config";
import useDragAndDrop from "../../../hooks/useDragAndDropV2";
import useAlignment from "../../../hooks/useAlignment";
import useAddWidget from "../../../hooks/useAddWidget";
import useElementInnerHtml from "../../../hooks/useElementInnerHtml";
import { EditorContext } from "../../../containers/editor/EditorLayout";
import { useUndoRedo } from "../../../hooks/useUndoRedo";
import { hexToRGB } from "../../../_helpers/utils";
import DescriptionAutoResizingTextarea from "../../Common/DescriptionAutoResizingTextarea";

let style = Object.assign({}, global);

const ITEM = "item";
const SECTION = "section";

const InputFieldSection = ({ type, sectionIdx, itemIdx, checked = false }) => {
  const fieldFor = typeof itemIdx === "number" ? ITEM : SECTION;
  const uniqueInputId = `${Math.random().toString(36).substring(2, 9)}`;
  const fieldId =
    fieldFor === ITEM ? `custom-scope-item${type}-s${sectionIdx}-i${itemIdx}-${uniqueInputId}` : `custom-section${type}-s${sectionIdx}-${uniqueInputId}`;
  const fieldName =
    fieldFor === ITEM ? `scope-item${type}-s${sectionIdx}-i${itemIdx}-${uniqueInputId}` : `pricing-section${type}-s${sectionIdx}-${uniqueInputId}`;

  return (
    <div className={cx(style["radio-icon"], style["form-group"], style["mb-0"])}>
      <div
        className={cx(style["custom-control"], style["custom-control-inline"], style["form-check"], {
          [style["custom-radio"]]: type == "radio",
          [style["custom-checkbox"]]: type == "checkbox",
        })}>
        <input
          type={type}
          id={fieldId}
          name={fieldName}
          checked={checked}
          className={`form-check-input form-check-input ${
            fieldFor === ITEM ? "scope-of-work-item-input" : "pricing-table-section-input"
          }`}
        />
        <label className="form-check-label"></label>
      </div>
    </div>
  );
};

const CanvasHtmlData = ({ canvasConfigData }) => {
  const sectionHeaderStyle = {};
  if (canvasConfigData?.sectionHeaderColor) {
    sectionHeaderStyle.backgroundColor = hexToRGB(canvasConfigData.sectionHeaderColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionHeaderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }

  const sectionSubHeaderStyle = {};
  if (canvasConfigData?.sectionSubheaderColor) {
    sectionSubHeaderStyle.backgroundColor = hexToRGB(canvasConfigData.sectionSubheaderColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionSubHeaderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }

  const sectionBodyStyle = {};
  if (canvasConfigData?.sectionBodyColor) {
    sectionBodyStyle.backgroundColor = hexToRGB(canvasConfigData.sectionBodyColor);
  }
  if (canvasConfigData?.sectionBorderColor) {
    sectionBodyStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.sectionBorderColor)}`;
  }
  if (canvasConfigData?.sectionBodyContentColor) {
    sectionBodyStyle.color = hexToRGB(canvasConfigData.sectionBodyContentColor);
  }

  const grandTotalBodyStyle = {};
  if (canvasConfigData?.grandTotalBodyColor) {
    grandTotalBodyStyle.backgroundColor = hexToRGB(canvasConfigData.grandTotalBodyColor);
  }
  if (canvasConfigData?.grandTotalBorderColor) {
    grandTotalBodyStyle.border = `1px solid ${hexToRGB(canvasConfigData.grandTotalBorderColor)}`;
  }

  const grandTotalBorderStyle = {};
  if (canvasConfigData?.grandTotalBorderColor) {
    grandTotalBorderStyle.borderBottom = `1px solid ${hexToRGB(canvasConfigData.grandTotalBorderColor)}`;
  }

  // Content Color section
  const sectionHeaderContentStyle = {};
  if (canvasConfigData?.sectionHeaderContentColor) {
    sectionHeaderContentStyle.color = hexToRGB(canvasConfigData.sectionHeaderContentColor);
  }

  const sectionSubheaderContentStyle = {};
  if (canvasConfigData?.sectionSubheaderContentColor) {
    sectionSubheaderContentStyle.color = hexToRGB(canvasConfigData.sectionSubheaderContentColor);
  }

  const grandTotalBodyContentStyle = {};
  if (canvasConfigData?.grandTotalBodyContentColor) {
    grandTotalBodyContentStyle.color = hexToRGB(canvasConfigData.grandTotalBodyContentColor);
  }
  return (
    <div
      className={cx(style["w-100"], style["pricingTable_editor"], style["scopeOfWork_editor"])}
      style={canvasConfigData?.["font-family"] ? { fontFamily: canvasConfigData["font-family"] } : {}}>
      {canvasConfigData?.sections?.map((sectionInfo, sectionIdx) => (
        <div
          className={`mb-3 card pricing-table-section scope-of-work-section section-card-${sectionIdx}`}
          key={sectionIdx}
          style={
            canvasConfigData?.sectionBorderColor ? { border: `1px solid ${canvasConfigData.sectionBorderColor}` } : {}
          }>
          <div
            className={cx(
              style["d-flex"],
              style["justify-content-between"],
              style["align-items-center"],
              style["card-header"]
            )}
            style={sectionHeaderStyle}>
            <div
              className={cx(style["d-flex"], style["align-items-center"], {
                [style["canvas-pricing-table-inputbox"]]:
                  canvasConfigData?.optionalSection || sectionInfo?.optionalSection,
              })}>
              {(canvasConfigData?.optionalSection || sectionInfo?.optionalSection) && (
                <>
                  {(canvasConfigData?.sectionSelectionType === "multiple" || sectionInfo?.optionalSection) && (
                    <InputFieldSection type="checkbox" sectionIdx={sectionIdx} checked={sectionInfo?.active} />
                  )}
                  {canvasConfigData?.sectionSelectionType === "single" && !sectionInfo?.optionalSection && (
                    <InputFieldSection type="radio" sectionIdx={sectionIdx} checked={sectionInfo?.active} />
                  )}
                </>
              )}
              <h6
                className={cx(style["mb-0"], style["fw-7"], {
                  [style["ms-4"]]: !canvasConfigData?.optionalSection && !sectionInfo?.optionalSection,
                })}
                style={sectionHeaderContentStyle}>
                {sectionInfo.name}
              </h6>
            </div>
            {sectionInfo.estimated_time === "estimated_hours" && (<h6
              className={`mb-0 fw-7 d-flex pricing-amount ${sectionInfo.active ? "disable" : ""}`}
              style={sectionHeaderContentStyle}>
              <span className={style["pe-2"]}>Total</span>

              <div className={"section-total total-estimated-show"}>{sectionInfo.total} Hours</div>
            </h6>)}
          </div>
          <div className={cx(style["p-0"], style["card-body"])}>
            <div className={cx(style["item-label"])} style={sectionSubHeaderStyle}>
              <ul className={cx(style["d-flex"], style["align-items-center"])}>
                <li
                  className={cx(style["item-name-lable"], style["flex-grow-1"], style["ms-4"])}
                  style={sectionSubheaderContentStyle}>
                  Task Name
                </li>
                <li
                  className={cx(style["item-amount-lable"], style["text-right"])}
                  style={sectionSubheaderContentStyle}>
                  {sectionInfo.estimated_show}
                </li>
              </ul>
            </div>
            {sectionInfo?.items?.map((itemInfo, itemIdx) => (
              <div
                className={cx(style["item-details"], { [style["no-image"]]: !itemInfo?.data?.images?.length > 0 })}
                key={itemIdx}
                style={sectionBodyStyle}>
                <div className={cx(style["item-info"])}>
                  <ul className={cx(style["d-flex"])}>
                    <li className="">
                      {(sectionInfo?.optionalItem || itemInfo?.optionalItem) && (
                        <>
                          {(sectionInfo?.itemSelectionType === "multiple" ||
                            (itemInfo.optionalItem && itemInfo.itemSelectionType === "multiple")) && (
                            <InputFieldSection
                              type="checkbox"
                              sectionIdx={sectionIdx}
                              itemIdx={itemIdx}
                              checked={itemInfo?.active}
                            />
                          )}
                          {sectionInfo?.itemSelectionType === "single" && (
                            <InputFieldSection
                              type="radio"
                              sectionIdx={sectionIdx}
                              itemIdx={itemIdx}
                              checked={itemInfo?.active}
                            />
                          )}
                        </>
                      )}
                    </li>
                    <li className={cx(style["item-info-details"], style["flex-grow-1"])}>
                      <div className={cx(style["d-flex"], style["mb-2"], style["align-items-center"])}>
                        <span className={cx(style["fw-7"], style["item-name"])}>{itemInfo?.data?.task_name}</span>
                      </div>
                      <div>
                        {itemInfo?.data?.description && (
                          <div className={cx(style["mb-1"], style["pre-line-text"])} dangerouslySetInnerHTML={{ __html: itemInfo?.data?.description }}/>
                        )}
                      </div>
                    </li>
                    <li className={cx(style["item-amount-details"], style["text-right"])}>
                      <div className={`pricing-amount scope-of-work-estimated-time ${!itemInfo?.active ? "disable" : ""}`}>
                        {itemInfo.data?.estimated}
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};



const FloatingTooltipComponent = ({
  applyBoldFn,
  applyUnderLineFn,
  tooltipFor,
  handleMergeCells,
  handleSplitCells,
  handleBulletApply,
  handleNumberApply,
  handleAlignCells,
  bulletActive,
  numberingAcive,
  defaltFontBold,
  defaltFontUnderline,
  isBoldeAvailable,
  horizontalScroll,
  bulletDisable
}) => {
  const elementRef = useRef(null);

  const [alignPosition, setAlignPosition] = useState(tooltipFor?.alignPosition || "");

  let tableDiv = document.querySelector(".TMright-pane");

  useEffect(() => {
    setTimeout(() => {
      if (elementRef.current && tableDiv) {
        const elementRect = elementRef.current.getBoundingClientRect();
        const paneRect = tableDiv.getBoundingClientRect();

        // Check if the element has crossed the right pane and left pane
        if (elementRect.left <= paneRect.left || elementRect.right >= paneRect.right) {
          elementRef.current.parentElement.classList.add("invisible");
        } else {
          elementRef.current.parentElement.classList.remove("invisible");
        }
      }
    }, 50);
  }, [horizontalScroll]);

  return (
    <div id="dhpTableCellEditableTooltip" className={cx(style[""])} ref={elementRef}>
      <ul className={style["d-flex"]}>
        {tooltipFor?.formattingOpt && (
          <>
            <li
              className={cx(style["toolset-group"], {
                [style["active"]]: defaltFontBold,
                [style["disable"]]: isBoldeAvailable,
              })}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                applyBoldFn();
              }}>
              <div className={style["toolset-item"]}>
                <div className={cx(style["toolset-action"], style["custom-tooltip"])}>
                  <Icon icon="bold2" />
                  <div className={cx(style["custom-tooltip-content"], style["top"])}>Bold</div>
                </div>
              </div>
            </li>
            {tooltipFor?.enableBullet && (
              <li
                className={cx(style["toolset-group"], { [style["active"]]: bulletActive }, { [style["disable"]]: bulletDisable })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleBulletApply();
                }}>
                <div className={style["toolset-item"]}>
                  <div className={cx(style["toolset-action"], style["custom-tooltip"])}>
                    <Icon icon="ui-list" />
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Bullets</div>
                  </div>
                </div>
              </li>
            )}
            {tooltipFor?.enableNumbering && (
              <li
                className={cx(style["toolset-group"], { [style["active"]]: numberingAcive })}
                onMouseDown={(e) => {
                  e.preventDefault(); 
                  e.stopPropagation();
                  handleNumberApply();
                }}>
                <div className={style["toolset-item"]}>
                  <div className={cx(style["toolset-action"], style["custom-tooltip"])}>
                    <Icon icon="ui-numbered-list" />
                    <div className={cx(style["custom-tooltip-content"], style["top"])}>Numbering</div>
                  </div>
                </div>
              </li>
            )}
          </>
        )}
      </ul>
    </div>
  );
};

const ItemFields = ({
  itemValue,
  slug,
  sectionIdx,
  itemIdx,
  updateSectionValue,
  updateTableConfig,
  isEnable = true,
  fieldType = "text",
  maxLength,
  dropdownContainer,
  currData,
  maxMinLimt,
  selectedViewType,
  setEstimatedInputBlur,
  estimatedInputBlur,
  addSectionItem,
  ...props
}) => {
  const inputRef = useRef(null);
  const editorId = useRef(`editor-${sectionIdx}-${itemIdx}-${slug}-${Math.random().toString(36).substr(2, 9)}`);
  const inputValueRef = useRef();

  const tooltipBoundry = document.querySelector(".table-modal-wrap");

  const [inputFieldValue, setInputFieldValue] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [tooltipFor, setTooltipFor] = useState({});
  const [tooltipTarget, setTooltipTarget] = useState();
  const [showTooltip, setShowTooltip] = useState(false);
  const [isBoldeAvailable, setIsBoldeAvailable] = useState(false);
  const [defaltFontBold, setDefaltFontBold] = useState(false);
  const [defaltFontUnderline, setDefaltFontUnderline] = useState(false);
  const [numberingAcive, setNumberingAcive] = useState(false);
  const [bulletActive, setBulletActive] = useState(false);
  const [bulletDisable, setBulletDisable] = useState(false);
  const resetValue = 100;

  const resetTooltip = () => {
    setTooltipTarget();
    setTooltipFor({});
    setDefaltFontBold(false);
    setIsBoldeAvailable(false);
    setBulletActive(false);
    setNumberingAcive(false);
    setDefaltFontUnderline(false);
    setBulletDisable(false);
  };

  const handleChange = e => {
    // Allow changes if:
    // 1. No maxLength is set, OR
    // 2. The new value is shorter (deletion), OR
    // 3. The new value length is within maxLength

    const value = e.target.value;
    if (["estimated"].includes(slug)) {
      if (selectedViewType === "Estimated Hours") {
        const regex = /^\d*\.?\d{0,1}$/;
        if (!regex.test(value)) return;
        setInputFieldValue(value);
      } else {
        const regex = /^\d*$/;
        if (!regex.test(value)) return;
        setInputFieldValue(value);
      }
    } else if (["description"].includes(slug)) {
      setInputFieldValue(e.target.value);
      inputValueRef.current = e.target.value;
    } else {
      setInputFieldValue(e.target.value);
    }
  };

  const handleKeyDown = e => {
    if (["estimated"].includes(slug)) {
      if (selectedViewType !== "Estimated Hours" && e.key === ".") {
        e.preventDefault();
      }
    }
  };

  const resetBulletNumber = type => {
    setNumberingAcive(type == "number" ? true : false);
    setBulletActive(type == "bullet" ? true : false);
    // setBulletDisable(type == "bullet" ? false : false)
  };

  function preserveCaretDuringFormatting(action, reference) {
    const sel = window.getSelection();
    if (!sel.rangeCount) return action();

    const range = sel.getRangeAt(0);
    const startContainer = range.startContainer;
    const startOffset = range.startOffset;

    const activeEditor = document.activeElement.closest('[contenteditable="true"]');
  if (!activeEditor) return;


    // Identify the contenteditable ancestor
    const editor = activeEditor

    // Run your formatting function (may alter DOM)
    action();

    // Restore focus
    if (editor) editor.focus();

    // Restore caret near old position if possible
    const newSel = window.getSelection();
    const newRange = document.createRange();

    try {
      // Try to restore to the same container
      if (startContainer.isConnected) {
        newRange.setStart(startContainer, Math.min(startOffset, startContainer.length || 0));
        newRange.collapse(true);
      } else if (editor) {
        // Fallback: put caret at the end of editor
        newRange.selectNodeContents(editor);
        newRange.collapse(false);
      }

      newSel.removeAllRanges();
      newSel.addRange(newRange);
    } catch (e) {
      // Silent fallback
    }
  }

  // Flatten nested divs
  const flattenDiv = el => {
    let changed = true;
    while (changed) {
      changed = false;
      const nested = el.querySelectorAll("div > div");
      nested.forEach(inner => {
        const parent = inner.parentNode;
        if (parent && parent.tagName === "DIV" && inner.parentNode.childNodes.length === 1) {
          while (inner.firstChild) parent.insertBefore(inner.firstChild, inner);
          inner.remove();
          changed = true;
        }
      });
    }
  };

  function cleanEditor(editor) {
    if (!editor) return;

    // Remove empty <span> but never remove the parent editor
    editor.querySelectorAll("span").forEach(span => {
      // Never delete contenteditable root
      if (span === editor) return;

      const text = span.textContent.trim();
      if (!text) {
        span.remove(); // remove empty span
      } else {
        const style = span.getAttribute("style") || "";
        // If span has no bold style, unwrap it
        if (!/font-weight\s*:\s*700/.test(style)) {
          span.replaceWith(...span.childNodes);
        }
      }
    });

    // Remove empty <div> elements safely
    editor.querySelectorAll("div").forEach(div => {
      if (div === editor) return; // never touch the root
      const html = div.innerHTML.replace(/&nbsp;/g, "").trim();
      if (!html) {
        div.remove();
      }
    });

    // Remove empty text nodes (not inside root text)
    [...editor.childNodes].forEach(node => {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
        node.remove();
      }
    });

    // Normalize invalid nesting (like <span><ul>...</ul></span>)
    editor.querySelectorAll("span > ul, span > div").forEach(child => {
      const span = child.parentElement;
      if (span && span !== editor) {
        span.replaceWith(...span.childNodes);
      }
    });
  }

  const expandRangeToBoldBoundaries = range => {
    // Expand selection start to include entire bold span
    let startNode = range.startContainer;
    let startBold =
      startNode.nodeType === 3
        ? startNode.parentNode.closest('span[style*="font-weight"]')
        : startNode.closest('span[style*="font-weight"]');

    if (startBold) {
      range.setStartBefore(startBold);
    }

    // Expand selection end to include entire bold span
    let endNode = range.endContainer;
    let endBold =
      endNode.nodeType === 3
        ? endNode.parentNode.closest('span[style*="font-weight"]')
        : endNode.closest('span[style*="font-weight"]');

    if (endBold) {
      range.setEndAfter(endBold);
    }
  };

   const selectedPartFontFormatting = fontFormat => {
    let selectedPart, sel, parentEl, activeHtml, range, html, rangeParentElement;
    let fontWeight = defaltFontBold ? 400 : 700;
    let fontStyle = "italic";
    let fontDecoration = defaltFontUnderline ? "none" : "underline";
    let removeSelection = false;

    if (window.getSelection) {
      sel = window.getSelection();
      let selectedCount = sel.toString().trim();
      if (selectedCount.length == 0) {
        return;
      }

      const activeEditor = document.activeElement.closest('[contenteditable="true"]');
      if (!activeEditor) return;

      if (window.getSelection) {
        if (sel.rangeCount) {
          parentEl = sel.getRangeAt(0).commonAncestorContainer;
          if (parentEl.nodeType != 1) {
            parentEl = parentEl.parentNode;
          }
        }
      }

      if (sel.rangeCount) {
        if (parentEl.innerText.trim() === sel.toString().trim() && !parentEl.hasAttribute("contenteditable")) {
          selectedPart = parentEl;
        } else {
          let container = document.createElement("div");
          for (let i = 0, len = sel.rangeCount; i < len; ++i) {
            container.appendChild(sel.getRangeAt(i).cloneContents());
          }

          let listItems = container.querySelectorAll("li");
          listItems.forEach(li => {
            if (!li.textContent.trim()) {
              li.remove();
            }
          });
          activeHtml = container.innerHTML;
          selectedPart = document.createElement("span");
          selectedPart.innerHTML = activeHtml;
        }
      }

      if (sel.getRangeAt && sel.rangeCount) {
        range = window.getSelection().getRangeAt(0);

        rangeParentElement = range.commonAncestorContainer;

        if (fontFormat === "bold") {
          if (!sel.rangeCount) return;
          const range = sel.getRangeAt(0);
          expandRangeToBoldBoundaries(range);

          let cursorTimeout;

          const placeCursorAndReturn = () => {
            // Clear any existing timeout
            if (cursorTimeout) {
              clearTimeout(cursorTimeout);
            }
            
            cursorTimeout = setTimeout(() => {
              const editor = activeEditor;
              if (!editor) return;

              editor.normalize();

              const sel = window.getSelection();
              if (!sel) {
                editor.focus();
                return;
              }

              if (!sel.rangeCount) {
                const r = document.createRange();
                r.selectNodeContents(editor);
                r.collapse(false);
                sel.addRange(r);
                editor.focus();
                return;
              }

              let anchorNode = sel.anchorNode;
              if (anchorNode && anchorNode.nodeType === Node.TEXT_NODE) {
                anchorNode = anchorNode.parentNode;
              }

              if (!anchorNode || !editor.contains(anchorNode)) {
                const r = document.createRange();
                r.selectNodeContents(editor);
                r.collapse(false);
                sel.removeAllRanges();
                sel.addRange(r);
              }

              editor.focus();
            }, 10);
          };

          const commonAncestor = range.commonAncestorContainer;
          const node = commonAncestor.nodeType === 1 ? commonAncestor : commonAncestor.parentNode;

          const rangeDivs = Array.from(activeEditor.querySelectorAll("div")).filter(div => {
            const r = document.createRange();
            r.selectNodeContents(div);
            return (
              range.compareBoundaryPoints(Range.END_TO_START, r) < 0 &&
              range.compareBoundaryPoints(Range.START_TO_END, r) > 0
            );
          });

          const singleDiv = node.closest("div");
          if (
            singleDiv &&
            !singleDiv.closest("li") &&
            sel.toString().trim().length > 0 &&
            sel.toString().trim() !== singleDiv.innerText.trim() &&
            rangeDivs.length <= 1
          ) {
            const selectedText = sel.toString();

            const rangeHTML = range.cloneContents();
            const container = document.createElement("div");
            container.appendChild(rangeHTML);
            const hasBold = container.querySelector("span[style*='font-weight:700']");

            let replacementHTML = "";
            if (hasBold) {
              replacementHTML = selectedText;
            } else {
              replacementHTML = `<span style="font-weight:700;">${selectedText}</span>`;
            }

            const span = document.createElement("span");
            span.innerHTML = replacementHTML;
            range.deleteContents();
            range.insertNode(span);

            singleDiv.normalize();

            const sel2 = window.getSelection();
            sel2.removeAllRanges();
            const newRange = document.createRange();
            newRange.selectNodeContents(span);
            newRange.collapse(false);
            sel2.addRange(newRange);

            placeCursorAndReturn();
            return;
          }

          const invalidAncestor = node.closest('span[style*="font-weight"] ul, span[style*="font-weight"] ol');
          if (invalidAncestor) {
            const span = invalidAncestor.closest("span");
            if (span) span.replaceWith(...span.childNodes);
            placeCursorAndReturn();
            return;
          }

          const parentUL = node.closest("ul, ol");
          const selectedText = sel.toString().trim();
          if (!selectedText){ 
            placeCursorAndReturn();
            return
          };

          if (parentUL) {
            const allLis = Array.from(parentUL.querySelectorAll("li"));
            const affectedLis = allLis.filter(li => {
              const liRange = document.createRange();
              liRange.selectNodeContents(li);
              return (
                range.compareBoundaryPoints(Range.END_TO_START, liRange) < 0 &&
                range.compareBoundaryPoints(Range.START_TO_END, liRange) > 0
              );
            });

            if (affectedLis.length > 0) {
              const getFirstDescendant = node => {
                let n = node;
                while (n && n.firstChild) n = n.firstChild;
                return n;
              };
              const getLastDescendant = node => {
                let n = node;
                while (n && n.lastChild) n = n.lastChild;
                return n;
              };

              const selRange = range.cloneRange();

              for (let i = 0; i < affectedLis.length; i++) {
                const li = affectedLis[i];
                const innerDiv = li.querySelector("div") || li;
                if (!innerDiv) continue;

                const liRange = document.createRange();
                liRange.selectNodeContents(innerDiv);

                const fullySelected = (() => {
                  try {
                    const liRangeCheck = document.createRange();
                    liRangeCheck.selectNodeContents(innerDiv);

                    if (
                      selRange.compareBoundaryPoints(Range.START_TO_START, liRangeCheck) <= 0 &&
                      selRange.compareBoundaryPoints(Range.END_TO_END, liRangeCheck) >= 0
                    ) {
                      return true;
                    }

                    const selText = selRange.toString().trim();
                    const divText = (innerDiv.textContent || "").trim();
                    if (selText && divText && selText === divText) {
                      return true;
                    }

                    const walker = document.createTreeWalker(innerDiv, NodeFilter.SHOW_TEXT, null, false);
                    let tn;
                    while ((tn = walker.nextNode())) {
                      const tR = document.createRange();
                      tR.selectNodeContents(tn);

                      if (
                        selRange.compareBoundaryPoints(Range.START_TO_START, tR) > 0 ||
                        selRange.compareBoundaryPoints(Range.END_TO_END, tR) < 0
                      ) {
                        return false;
                      }
                    }

                    return true;
                  } catch (e) {
                    return false;
                  }
                })();

                const firstAffected = i === 0;
                const lastAffected = i === affectedLis.length - 1;

                if (fullySelected) {
                  const html = innerDiv.innerHTML.trim();
                  if (!html) continue; 
                  const hasBold = /<span[^>]*font-weight:\s*700[^>]*>/.test(html);
                  if (hasBold) {
                    innerDiv.innerHTML = html.replace(/<span[^>]*font-weight:\s*700;?[^>]*>(.*?)<\/span>/gi, "$1");
                  } else {
                    innerDiv.innerHTML = `<span style="font-weight:700;">${html}</span>`;
                  }
                  innerDiv.normalize();
                  continue;
                }

                if (firstAffected || lastAffected) {
                  const subRange = document.createRange();

                  if (firstAffected) {
                  
                    subRange.setStart(selRange.startContainer, selRange.startOffset);

                    const lastNode = getLastDescendant(innerDiv) || innerDiv;
                    if (lastNode.nodeType === Node.TEXT_NODE) {
                      subRange.setEnd(lastNode, lastNode.nodeValue ? lastNode.nodeValue.length : 0);
                    } else {
                      subRange.setEndAfter(lastNode);
                    }
                  }

                  if (lastAffected) {
                   
                    const firstNode = getFirstDescendant(innerDiv) || innerDiv;
                    if (firstNode.nodeType === Node.TEXT_NODE) {
                      subRange.setStart(firstNode, 0);
                    } else if (firstNode) {
                      subRange.setStartBefore(firstNode);
                    } else {
                      subRange.setStartBefore(innerDiv);
                    }
                    subRange.setEnd(selRange.endContainer, selRange.endOffset);
                  }

                  if (firstAffected && lastAffected) {
                    
                    subRange.setStart(selRange.startContainer, selRange.startOffset);
                    subRange.setEnd(selRange.endContainer, selRange.endOffset);
                  }           
                  const subClone = subRange.cloneContents();
                  const probe = document.createElement('div');
                  probe.appendChild(subClone);

                  
                  const cloneHasBold = !!probe.querySelector('b, strong, span[style*="font-weight"], .bold, .fw-bold');

                
                  const nodeHasBoldAncestor = (node, stopAt) => {
                    if (!node) return false;
                    let cur = node.nodeType === 3 ? node.parentNode : node;
                    while (cur && cur !== stopAt && cur !== document) {
                      if (cur.matches && cur.matches('b, strong')) return true;
                      const style = cur.getAttribute ? (cur.getAttribute('style') || '') : '';
                      if (/font-weight\s*:\s*(?:7\d{2}|bold)/i.test(style)) return true;
                      if (cur.classList && (cur.classList.contains('bold') || cur.classList.contains('fw-bold'))) return true;
                      cur = cur.parentNode;
                    }
                    return false;
                  };

                  const boldIntersectsRange = r => {
                    try {
                      const cac = r.commonAncestorContainer.nodeType === 1 ? r.commonAncestorContainer : r.commonAncestorContainer.parentNode;
                      if (!cac) return false;
                      const boldEls = Array.from(cac.querySelectorAll('b, strong, span[style*="font-weight"], .bold, .fw-bold'));
                      for (let i = 0; i < boldEls.length; i++) {
                        const el = boldEls[i];
                        const elRange = document.createRange();
                        elRange.selectNodeContents(el);
                        const endsBefore = r.compareBoundaryPoints(Range.END_TO_START, elRange) >= 0;
                        const startsAfter = r.compareBoundaryPoints(Range.START_TO_END, elRange) <= 0;
                        if (!endsBefore && !startsAfter) return true;
                      }
                    } catch (e) {}
                    return false;
                  };

                  const subRangeHasBold = (
                    cloneHasBold ||
                    nodeHasBoldAncestor(subRange.startContainer, subRange.commonAncestorContainer) ||
                    nodeHasBoldAncestor(subRange.endContainer, subRange.commonAncestorContainer) ||
                    boldIntersectsRange(subRange)
                  );

                  (function unwrapIntersectingBoldInLiveDOM(rng) {
                    try {
                      const cac = rng.commonAncestorContainer.nodeType === 1
                        ? rng.commonAncestorContainer
                        : rng.commonAncestorContainer.parentNode;
                      if (!cac) return;
                      const boldEls = Array.from(
                        cac.querySelectorAll('b, strong, span[style*="font-weight"], .bold, .fw-bold')
                      );
                      for (const el of boldEls) {
                        const elRange = document.createRange();
                        elRange.selectNodeContents(el);
                        const endsBefore = rng.compareBoundaryPoints(Range.END_TO_START, elRange) >= 0;
                        const startsAfter = rng.compareBoundaryPoints(Range.START_TO_END, elRange) <= 0;
                        if (!endsBefore && !startsAfter) {
                          while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
                          el.remove();
                        }
                      }
                    } catch (e) {
                      /* ignore safely */
                    }
                  })(subRange);


                const frag = subRange.extractContents();

                const tmp = document.createElement('div');
                tmp.appendChild(frag.cloneNode(true));
                const tmpClone = tmp.cloneNode(true);

                const fragText = (tmp.textContent || '').trim();

                if (!fragText) {
                  continue;
                }

                let replacementNode;
                if (subRangeHasBold) {
                  
                  tmpClone.querySelectorAll('span[style*="font-weight"]').forEach(node => {
                    const tag = node.tagName ? node.tagName.toLowerCase() : '';
                    let isBold = false;
                    if (node.getAttribute) {
                      const style = node.getAttribute('style') || '';
                      if (/font-weight\s*:\s*(?:7\d{2}|bold)/i.test(style)) isBold = true;
                    }

                    if (isBold) {
                    
                      while (node.firstChild) node.parentNode.insertBefore(node.firstChild, node);
                      node.remove();
                    }
                  });

                  tmp.querySelectorAll('span').forEach(s => { if (!s.textContent.trim()) s.remove(); });

                  const fragment = document.createDocumentFragment();
                  while (tmpClone.firstChild) fragment.appendChild(tmpClone.firstChild);
                  replacementNode = fragment;
                } else {
                  const span = document.createElement('span');
                  span.setAttribute('style', 'font-weight:700;');
                  span.appendChild(frag);
                  replacementNode = span;
                }

                subRange.insertNode(replacementNode);
                innerDiv.normalize();

                if (lastAffected) {
                  const sel2 = window.getSelection();
                  sel2.removeAllRanges();
                  const newRange = document.createRange();
                  try {
                    newRange.selectNodeContents(innerDiv);
                    newRange.collapse(false);
                    sel2.addRange(newRange);
                  } catch (e) {
                    const fallback = document.createRange();
                    fallback.selectNodeContents(parentUL || innerDiv);
                    fallback.collapse(false);
                    sel2.removeAllRanges();
                    sel2.addRange(fallback);
                  }
                }

                continue;
                }
              }

              const topUL = parentUL;
              if (topUL) topUL.normalize();

              placeCursorAndReturn();
              return;
            } else if (affectedLis.length > 1) {
              const allAlreadyBold = affectedLis.every(li => {
                const div = li.querySelector("div");
                return div && div.querySelector("span[style*='font-weight:700']");
              });

              affectedLis.forEach(li => {
                const div = li.querySelector("div");

                if (!div) {
                  let html = li.innerHTML.trim();
                  if (!html) return;

                  const hasBoldSpan = /<span[^>]*font-weight:\s*700[^>]*>/i.test(html);

                  if (hasBoldSpan) {
                    li.innerHTML = li.innerHTML.replace(/<span[^>]*font-weight:\s*700;?[^>]*>(.*?)<\/span>/gi, "$1");
                  } else {
                    if (/<br\s*\/?>/i.test(html)) {
                      const parts = html.split(/(<br\s*\/?>)/gi);
                      const built = parts
                        .map(seg => {
                          if (/^<br/i.test(seg)) return seg;
                          const trimmed = seg.trim();
                          return trimmed === "" ? "" : `<span style="font-weight:700;">${trimmed}</span>`;
                        })
                        .join("");
                      li.innerHTML = built;
                    } else {
                      li.innerHTML = `<span style="font-weight:700;">${html}</span>`;
                    }
                  }
                  placeCursorAndReturn();
                  return;
                }

                if (allAlreadyBold) {
                  div.innerHTML = div.innerHTML.replace(/<span[^>]*font-weight:\s*700;?[^>]*>(.*?)<\/span>/gi, "$1");
                } else {
                  if (!div.querySelector("span[style*='font-weight:700']")) {
                    div.innerHTML = `<span style="font-weight:700;">${div.innerHTML}</span>`;
                  }
                }
              });
            }
          } else {
            const parentDiv = node.closest("div");
            if (parentDiv && !parentDiv.closest("li")) {
              const selection = window.getSelection();
              if (!selection.rangeCount) return;
              const selRange = selection.getRangeAt(0).cloneRange();

              if (!selRange.toString().trim()) return;

              const editor = activeEditor;

              const startDiv =
                selRange.startContainer.nodeType === 3
                  ? selRange.startContainer.parentNode.closest("div")
                  : selRange.startContainer.closest("div");
              const endDiv =
                selRange.endContainer.nodeType === 3
                  ? selRange.endContainer.parentNode.closest("div")
                  : selRange.endContainer.closest("div");

              if (!startDiv || !endDiv || !editor.contains(startDiv) || !editor.contains(endDiv)) {
                placeCursorAndReturn();
                return;
              }

              const liveDivs = [];
              let cur = startDiv;
              while (cur) {
                if (cur.tagName === "DIV" && editor.contains(cur)) liveDivs.push(cur);
                if (cur === endDiv) break;
                cur = cur.nextElementSibling;
              }
              if (liveDivs.length === 0) return;

              const cloned = selRange.cloneContents();
              const tmpCheck = document.createElement("div");
              tmpCheck.appendChild(cloned);
              const selectionHasBold = !!tmpCheck.querySelector("span[style*='font-weight:700']");

              const cleanNode = node => {
                node.querySelectorAll("span").forEach(s => {
                  if (!s.textContent.trim()) s.remove();
                });
                node.querySelectorAll("div").forEach(d => {
                  if (!d.textContent.trim() && d.children.length === 0) d.remove();
                });
                node.normalize();
              };

              const first = liveDivs[0];
              const last = liveDivs[liveDivs.length - 1];


              if (liveDivs.length === 1) {
                const singleSub = document.createRange();
                singleSub.setStart(selRange.startContainer, selRange.startOffset);
                singleSub.setEnd(selRange.endContainer, selRange.endOffset);

                const frag = singleSub.cloneContents();
                const tmp = document.createElement("div");
                tmp.appendChild(frag);

                const fragHasBold = !!tmp.querySelector("span[style*='font-weight:700']");

                if (fragHasBold) {
                  const extracted = singleSub.extractContents();
                  const temp = document.createElement("div");
                  temp.appendChild(extracted.cloneNode(true));

                  temp.querySelectorAll("span[style*='font-weight:700']").forEach(s => {
                    while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
                    s.remove();
                  });

                  temp.querySelectorAll("span").forEach(s => {
                    if (!s.textContent.trim()) s.remove();
                  });

                  singleSub.insertNode(temp.cloneNode(true));
                  first.normalize();
                  cleanNode(first);
                } else {
                  const extracted = singleSub.extractContents();
                  const wrapper = document.createElement("span");
                  wrapper.setAttribute("style", "font-weight:700;");
                  wrapper.appendChild(extracted);
                  singleSub.insertNode(wrapper);
                  first.normalize();
                  cleanNode(first);
                }

                const sel2 = window.getSelection();
                sel2.removeAllRanges();
                const newRange = document.createRange();
                newRange.selectNodeContents(first);
                newRange.collapse(false);
                sel2.addRange(newRange);
                editor.focus();
                placeCursorAndReturn();
                return;
              }

              if (first) {
                const rStart = document.createRange();
                rStart.setStart(selRange.startContainer, selRange.startOffset);

                let lastNodeOfFirst = first;
                while (lastNodeOfFirst && lastNodeOfFirst.lastChild) lastNodeOfFirst = lastNodeOfFirst.lastChild;
                if (lastNodeOfFirst.nodeType === Node.TEXT_NODE) {
                  rStart.setEnd(lastNodeOfFirst, lastNodeOfFirst.nodeValue ? lastNodeOfFirst.nodeValue.length : 0);
                } else {
                  rStart.setEndAfter(first);
                }

                const frag = rStart.cloneContents();
                const tmp = document.createElement("div");
                tmp.appendChild(frag);

                const selectionHasBold =
                  !!range.commonAncestorContainer.closest("span[style*='font-weight:700']") ||
                  !!range.startContainer.parentNode.closest("span[style*='font-weight:700']") ||
                  !!range.endContainer.parentNode.closest("span[style*='font-weight:700']");

                const fragHasBold = !!tmp.querySelector("span[style*='font-weight:700']");

                if (selectionHasBold || fragHasBold) {
                  const boldSpans = first.querySelectorAll("span[style*='font-weight:700']");
                  boldSpans.forEach(span => {
                    const spanRange = document.createRange();
                    spanRange.selectNodeContents(span);

                    if (
                      range.compareBoundaryPoints(Range.END_TO_START, spanRange) < 0 &&
                      range.compareBoundaryPoints(Range.START_TO_END, spanRange) > 0
                    ) {
                      while (span.firstChild) span.parentNode.insertBefore(span.firstChild, span);
                      span.remove();
                    }
                  });

                  first.normalize();
                  cleanNode(first);
                } else {
                  const extracted = rStart.extractContents();
                  const wrapper = document.createElement("span");
                  wrapper.setAttribute("style", "font-weight:700;");
                  wrapper.appendChild(extracted);
                  rStart.insertNode(wrapper);
                  first.normalize();
                  cleanNode(first);
                }
              }

              if (liveDivs.length > 2) {
                for (let i = 1; i < liveDivs.length - 1; i++) {
                  const d = liveDivs[i];
                  const innerHtml = d.innerHTML.trim();
                  if (!innerHtml) continue;

                  if (selectionHasBold) {
                    d.querySelectorAll("span[style*='font-weight:700']").forEach(s => {
                      while (s.firstChild) s.parentNode.insertBefore(s.firstChild, s);
                      s.remove();
                    });
                  } else {
                    const hasAny = !!d.querySelector("span[style*='font-weight:700']");
                    if (!hasAny) {
                      d.innerHTML = `<span style="font-weight:700;">${innerHtml}</span>`;
                    }
                  }
                  d.normalize();
                  cleanNode(d);
                }
              }

              if (last && last !== first) {
                const rEnd = document.createRange();

                let firstNodeOfLast = last;
                while (firstNodeOfLast && firstNodeOfLast.firstChild) firstNodeOfLast = firstNodeOfLast.firstChild;

                if (firstNodeOfLast && firstNodeOfLast.nodeType === Node.TEXT_NODE) {
                  rEnd.setStart(firstNodeOfLast, 0);
                } else if (firstNodeOfLast) {
                  rEnd.setStartBefore(firstNodeOfLast);
                } else {
                  rEnd.setStartBefore(last);
                }

                rEnd.setEnd(selRange.endContainer, selRange.endOffset);

                const frag2 = rEnd.cloneContents();
                const tmp2 = document.createElement("div");
                tmp2.appendChild(frag2);

                const selectionHasBold =
                  !!range.commonAncestorContainer.closest("span[style*='font-weight:700']") ||
                  !!range.startContainer.parentNode.closest("span[style*='font-weight:700']") ||
                  !!range.endContainer.parentNode.closest("span[style*='font-weight:700']");

                const frag2HasBold = !!tmp2.querySelector("span[style*='font-weight:700']");

                if (selectionHasBold || frag2HasBold) {
                  const boldSpans = last.querySelectorAll("span[style*='font-weight:700']");
                  boldSpans.forEach(span => {
                    while (span.firstChild) span.parentNode.insertBefore(span.firstChild, span);
                    span.remove();
                  });

                  last.normalize();
                  cleanNode(last);
                } else {
                  const extracted2 = rEnd.extractContents();
                  const wrapper2 = document.createElement("span");
                  wrapper2.setAttribute("style", "font-weight:700;");
                  wrapper2.appendChild(extracted2);
                  rEnd.insertNode(wrapper2);

                  last.normalize();
                  cleanNode(last);
                }
              }

              const sel2 = window.getSelection();
              sel2.removeAllRanges();
              const newRange = document.createRange();
              if (last) {
                newRange.selectNodeContents(last);
                newRange.collapse(false);
              } else if (first) {
                newRange.selectNodeContents(first);
                newRange.collapse(false);
              } else {
                newRange.selectNodeContents(editor);
                newRange.collapse(false);
              }
              sel2.addRange(newRange);
              editor.focus();
              placeCursorAndReturn();
              return;
            }
          }
          placeCursorAndReturn();
          return;
        }

        if (fontFormat === "bullet" || fontFormat === "number") {
          const currentClass = fontFormat === "bullet" ? "unorder-list" : "order-list";
          const listStyle = fontFormat === "bullet" ? "square" : "decimal";

          const sel = window.getSelection();
          if (!sel.rangeCount) return;
          const range = sel.getRangeAt(0);

          const normalizeToElement = node => (node.nodeType === Node.TEXT_NODE ? node.parentNode : node);

          let startNode = normalizeToElement(range.startContainer);
          let endNode = normalizeToElement(range.endContainer);

          const startDiv = startNode.closest("div");
          const endDiv = endNode.closest("div");

          const editor = activeEditor;
          if (!editor) return;

          let outerDiv = editor.querySelector(":scope > div");
          if (!outerDiv) {
            outerDiv = document.createElement("div");
            editor.appendChild(outerDiv);
          }

          const ancestorUL =
            range.commonAncestorContainer.nodeType === 1
              ? range.commonAncestorContainer.closest("ul")
              : normalizeToElement(range.commonAncestorContainer).closest("ul");

          if (
            ancestorUL &&
            (ancestorUL.classList.contains("unorder-list") || ancestorUL.classList.contains("order-list"))
          ) {
            const selectedLis = [];
            const selRange = range;
            const allLis = Array.from(ancestorUL.querySelectorAll("li"));

            allLis.forEach(li => {
              const liRange = document.createRange();
              liRange.selectNodeContents(li);
              if (
                selRange.compareBoundaryPoints(Range.END_TO_START, liRange) < 0 &&
                selRange.compareBoundaryPoints(Range.START_TO_END, liRange) > 0
              ) {
                selectedLis.push(li);
              }
            });

            if (selectedLis.length === 0) return;

            const startIndex = allLis.indexOf(selectedLis[0]);
            const endIndex = allLis.indexOf(selectedLis[selectedLis.length - 1]);

            const beforeLis = allLis.slice(0, startIndex);
            const selectedLisSlice = allLis.slice(startIndex, endIndex + 1);
            const afterLis = allLis.slice(endIndex + 1);

            const fragment = document.createDocumentFragment();

            if (beforeLis.length > 0) {
              const beforeUL = ancestorUL.cloneNode(false);
              beforeLis.forEach(li => beforeUL.appendChild(li));
              fragment.appendChild(beforeUL);
            }

            selectedLisSlice.forEach(li => {
              const div = document.createElement("div");
              if (li.querySelector("br")) {
                div.innerHTML = li.innerHTML;
              } else {
                div.innerHTML = li.innerHTML;
              }
              flattenDiv(div);
              fragment.appendChild(div);
            });

            if (afterLis.length > 0) {
              const afterUL = ancestorUL.cloneNode(false);
              afterLis.forEach(li => afterUL.appendChild(li));
              fragment.appendChild(afterUL);
            }

            ancestorUL.replaceWith(fragment);

            setTimeout(() => {
              const range = document.createRange();
              range.selectNodeContents(editor);
              range.collapse(false);

              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);

              editor.focus();
            }, 50);

            resetBulletNumber(fontFormat);

            return;
          }

          const divsToConvert = [];
          let node = startDiv;
          while (node) {
            if (node.nodeType === 1 && node.tagName === "DIV" && node.closest('[contenteditable="true"]') === editor) {
              divsToConvert.push(node);
            }
            if (node === endDiv) break;
            node = node.nextSibling;
          }

          if (divsToConvert.length === 0) return;

          const ul = document.createElement("ul");
          ul.className = currentClass;
          ul.style.listStyleType = listStyle;

          divsToConvert.forEach(d => {
            if (d.querySelector("ul, ol")) {
              return;
            }
            flattenDiv(d);

            const parts = d.innerHTML
              .split(/<br\s*\/?>/i)
              .map(p => p.trim())
              .filter(Boolean);

            if (parts.length > 1) {
              const li = document.createElement("li");
              if (d.querySelector("br")) {
                li.innerHTML = d.innerHTML;
              } else {
                const innerDiv = document.createElement("div");
                innerDiv.innerHTML = d.innerHTML;
                li.appendChild(innerDiv);
              }
              ul.appendChild(li);
            } else {
              const li = document.createElement("li");
              const innerDiv = document.createElement("div");
              innerDiv.innerHTML = d.innerHTML;
              li.appendChild(innerDiv);
              ul.appendChild(li);
            }
          });

          const firstDiv = divsToConvert[0];
          const lastDiv = divsToConvert[divsToConvert.length - 1];

          if (!firstDiv || !lastDiv || firstDiv === editor || lastDiv === editor) {
            editor.appendChild(ul);
          } else {
            const rangeToReplace = document.createRange();
            rangeToReplace.setStartBefore(firstDiv);
            rangeToReplace.setEndAfter(lastDiv);

            rangeToReplace.deleteContents();
            rangeToReplace.insertNode(ul);
          }

          editor.normalize();

          setTimeout(() => {
            const lastLi = editor.querySelector("ul li:last-child");
            const range = document.createRange();

            if (lastLi) {
              range.selectNodeContents(lastLi);
              range.collapse(false);
            } else {
              range.selectNodeContents(editor);
              range.collapse(false);
            }

            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
            editor.focus();
          }, 50);

          resetBulletNumber(fontFormat);

          return;
        }

        if (range.commonAncestorContainer.closest('[contenteditable="true"]') === range.commonAncestorContainer) {
        } else {
          range.deleteContents();
        }

        let el = document.createElement("div");
        el.innerHTML = html;

        let frag = document.createDocumentFragment(),
          node,
          lastNode;
        while ((node = el.firstChild)) {
          lastNode = frag.appendChild(node);
        }

        if (
          rangeParentElement.tagName === "LI" ||
          (rangeParentElement.tagName === "SPAN" && ["bullet", "number"].includes(fontFormat))
        ) {
          if (rangeParentElement.textContent.trim() === "") {
            rangeParentElement.parentNode.removeChild(rangeParentElement);
          }
        } else if (rangeParentElement.tagName === "UL") {
          const liElements = rangeParentElement.querySelectorAll("li");
          liElements.forEach(li => {
            if (li.textContent.trim() === "") {
              li.parentNode.removeChild(li);
            }
          });
        }
        range.insertNode(frag);

        if (removeSelection) window.getSelection().removeAllRanges();
      }
    }
  };

  const applyBoldFn = () => {
    // selectedPartFontFormatting("bold");
    preserveCaretDuringFormatting(() => selectedPartFontFormatting("bold"));
  };

  const handleBulletApply = () => {
    preserveCaretDuringFormatting(() => selectedPartFontFormatting("bullet"));
  };

  const handleBlur = (slug, sectionIdx, itemIdx) => {
    const compareTo = slug === "description" ? inputValueRef.current : inputFieldValue;
    if (itemValue === compareTo) return;
    let appliedValue = inputFieldValue;

    if(slug === "estimated"){
      setEstimatedInputBlur(!estimatedInputBlur)
    }

    if (slug === "description") {
      appliedValue = inputValueRef.current;
      setInputFieldValue(appliedValue);
    }

    if (typeof sectionIdx === "number" && typeof itemIdx === "number") {
      updateSectionValue({ [slug]: appliedValue }, sectionIdx, itemIdx);
    } 
    setIsFocused(false);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setTooltipOpen(false); // Hide tooltip immediately on focus
  };

  const handleMouseEnter = () => {
    if (inputRef.current && !isFocused) {
      setTooltipOpen(inputRef.current.scrollWidth > inputRef.current.clientWidth);
    }
  };

  useEffect(() => {
    if (["estimated"].includes(slug)) {
      if (selectedViewType !== "Estimated Hours" && selectedViewType !== "Ongoing" && inputFieldValue)
        setInputFieldValue("0");
      else if (selectedViewType !== "Estimated Hours" && selectedViewType === "Ongoing" && inputFieldValue)
        setInputFieldValue("-");
      else setInputFieldValue("");
    }
  }, [selectedViewType]);

  useEffect(() => {
    if (isEnable) setInputFieldValue(itemValue);
    else setInputFieldValue("-");
  }, [itemValue, isEnable]);

  return (
    <>
      {fieldType != "textarea" && (
        <>
          <Input
            type={fieldType}
            value={inputFieldValue}
            onChange={e => handleChange(e)}
            onFocus={handleFocus}
            onBlur={e => handleBlur(slug, sectionIdx, itemIdx)}
            disabled={!isEnable}
            isEnable={!isEnable}
            {...props}
            innerRef={inputRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setTooltipOpen(false)}
            onMouseDown={() => setTooltipOpen(false)}
            onKeyDown={e => handleKeyDown(e)}
            dropdownContainer={dropdownContainer ? dropdownContainer : undefined}
          />
          <Tooltip isOpen={tooltipOpen} target={inputRef.current} placement={"bottom-end"}>
            {inputFieldValue}
          </Tooltip>
        </>
      )}
      {fieldType === "textarea" && (
        <DescriptionAutoResizingTextarea
          id={editorId.current} 
          inputFieldValue={inputFieldValue}
          slug={slug}
          sectionIdx={sectionIdx}
          itemIdx={itemIdx}
          handleBlur={handleBlur}
          handleChange={handleChange}
          setTooltipTarget={setTooltipTarget}
          setTooltipFor={setTooltipFor}
          setShowTooltip={setShowTooltip}
          showTooltip={showTooltip}
          setDefaltFontBold={setDefaltFontBold}
          setBulletActive={setBulletActive}
          setBulletDisable={setBulletDisable}
          maxCharacter={1000}
          placeHolderShow 
          defaultHeight={100}
          minHeight={48}
          maxHeight={200}
          addSectionItem={addSectionItem}
          {...props}
        />
      )}
      {tooltipTarget && (
        <Tooltip
          key={`tooltip-${tooltipTarget.style.top}-${tooltipTarget.style.left}`}
          placement="top-start"
          target={tooltipTarget}
          isOpen={showTooltip}
          className="table-editable-tooltip"
          boundariesElement={tooltipBoundry}
          hideArrow={true}>
          <FloatingTooltipComponent
            tooltipFor={tooltipFor}
            applyBoldFn={applyBoldFn} 
            handleBulletApply={handleBulletApply}
            bulletActive={bulletActive}
            numberingAcive={numberingAcive}
            defaltFontBold={defaltFontBold}
            bulletDisable={bulletDisable}
            isBoldeAvailable={isBoldeAvailable}
          />
        </Tooltip>
      )}
    </>
  );
};

const ToggleDropdownItem = ({ toggleId, label, selectfn, checked = false, disabled = false }) => {
  return (
    <>
      <div className={cx(style["form-check"], style["form-switch"], { [style["disable"]]: disabled })}>
        <input
          type="checkbox"
          className={style["form-check-input"]}
          defaultChecked={checked}
          id={toggleId}
          onClick={selectfn}
          disabled={disabled}
        />
        <label className={cx(style["form-check-label"], style["font-weight-normal"])} htmlFor={toggleId}>
          {label}
        </label>
      </div>
    </>
  );
};

const ItemDropdown = ({
  tableConfigData,
  updateTableConfig,
  sectionIdx,
  itemIdx,
  deleteSectionItem,
  itemInfo,
  updateSectionValue,
  dropdownContainer,
  customDropdownMenuClass,
}) => {
  const dropdownRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  // Toggle the dropdown
  const toggle = e => {
    if (e.target.closest(".scope-of-work-custom-dropdown")) setIsOpen(true);
    else setIsOpen(prevState => !prevState);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const currentDropdown = document.querySelectorAll(".scope-of-work-custom-dropdown.show")[0];
        if (currentDropdown) {
          // Check if menu has placed top of the dropdown and container present
          if (dropdownRef?.current && dropdownRef.current?.menuRef?.current && dropdownContainer) {
            const parentDropdown = dropdownRef.current.containerRef.current;
            const menuElement = dropdownRef.current.menuRef.current;

            const parentDropdownStyle = parentDropdown.getBoundingClientRect();
            const menuElementStyle = menuElement.getBoundingClientRect();
            if (menuElementStyle?.top < parentDropdownStyle?.top) {
              let calculatedTop = parentDropdownStyle.top + parentDropdownStyle.height + 2;

              menuElement.style.transform = `translate3d(${menuElementStyle.left}px, ${calculatedTop}px, 0px)`;
              menuElement.style.opacity = 1;
            }
            menuElement.style.opacity = 1;
          }
        }
      }, 15);
    }
  }, [isOpen]);

  return (
    <Dropdown ref={dropdownRef} isOpen={isOpen} toggle={toggle}>
      <DropdownToggle tag="a" className={cx(style["dropdown-toggle"], style["custom-tooltip"])}>
        <Icon icon="ui-more" />
        <div className={cx(style["custom-tooltip-content"], style["top"])}>More</div>
      </DropdownToggle>
      <DropdownMenu
        right
        ref={dropdownRef}
        className={cx(style["shadow"], style["border-0"], style["rounded-sm"], "custom-pricing-dropdown", "scope-of-work-custom-dropdown", {
          [customDropdownMenuClass]: customDropdownMenuClass,
        })}
        container={dropdownContainer ? dropdownContainer : undefined}
        style={dropdownContainer && { opacity: 0 }}>
        <DropdownItem tag="span">
          <ToggleDropdownItem
            toggleId={`optionalItem-${sectionIdx}-${itemIdx}`}
            label={"Optional Item"}
            checked={
              tableConfigData?.sections?.[sectionIdx]?.items[itemIdx]?.optionalItem ||
              tableConfigData?.sections?.[sectionIdx]?.optionalItem
            }
            disabled={tableConfigData?.sections?.[sectionIdx]?.optionalItem}
            selectfn={() =>
              updateTableConfig(
                { optionalItem: !tableConfigData?.sections?.[sectionIdx]?.items[itemIdx]?.optionalItem },
                "items",
                sectionIdx,
                itemIdx
              )
            }
          />
        </DropdownItem>
        <DropdownItem divider />
        <DropdownItem tag="span" className={style["font-14"]}>
          <div
            className={cx(style["d-flex"], style["align-items-center"], {
              [style["disable"]]: tableConfigData.sections[sectionIdx].items.length < 2,
              [style["cursor-pointer"]]: tableConfigData.sections[sectionIdx].items.length > 1,
            })}
            onClick={() => deleteSectionItem("item", sectionIdx, itemIdx)}>
            <Icon icon="ui-trash" additionalclass={"me-2"} />
            <span>Delete Task</span>
          </div>
          {tableConfigData.sections[sectionIdx].items.length < 2 && (
            <div className={cx(style["ms-2"])}>
              <Icon icon="ui-info" additionalclass={"font-14"} id={`itemTooltip-${sectionIdx}-${itemIdx}`} />
              <UncontrolledTooltip placement="bottom" target={`itemTooltip-${sectionIdx}-${itemIdx}`}>
                You have only one task in the <br></br> section. Hence it can’t be deleted
              </UncontrolledTooltip>
            </div>
          )}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

const SectionComponent = ({
  sectionInfo,
  sectionIdx,
  tableConfigData,
  handleBlur,
  isEditable,
  handleEditClick,
  changeSectionName,
  onKeyDownFn,
  moveSection,
  updateTableConfig,
  deleteSectionItem,
  updateSectionValue,
  addSectionItem,
  setTableConfigData,
  dropdownContainer,
  modalFullScreen,
  dropdownOpen,
  setDropdownOpen,
  defaultEstimatedViews,
  viewTypes,
  setViewTypes,
  setEstimatedInputBlur,
  estimatedInputBlur,
  totalEstimatedHours
}) => {
  const selectedViewType = defaultEstimatedViews?.find(
    view => view.value === tableConfigData["sections"][0].estimated_time
  )?.name;
  const [sectionItems, setSectionItems] = useState(sectionInfo.items);
  const [readyToUpdate, setReadyToUpdate] = useState(false);
  const [sectionActualWidth, setSectionActualWidth] = useState(0);
  const [insertWidth, setInsertWidth] = useState(false);
  const [itemNameDropdown, setItemNameDropdown] = useState(false);
  const [tooltipId, setTooltipId] = useState();
  const [itemTooltipValue, setItemTooltipValue] = useState();

  const sectionRef = useRef(null);

  const { items: dragItems, containerProps, getItemProps } = useDragAndDrop(sectionItems, "vertical", setReadyToUpdate);

  const updateSelectionType = type => {
    let updatedTable = { ...tableConfigData };
    if (type === "single") {
      updatedTable.sections.forEach((section, sectionIndex) => (section.active = sectionIndex === sectionIdx));
    } else {
      updatedTable.sections[sectionIdx].active = !updatedTable.sections[sectionIdx].active;
    }
    setTableConfigData(updatedTable);
  };

  const toggle = () => setDropdownOpen(prevState => !prevState);

  const updateItemSelectionType = (type, itemIdx) => {
    let updatedTable = { ...tableConfigData };
    if (type === "single") {
      updatedTable.sections[sectionIdx]?.items.forEach((item, itemIndex) => (item.active = itemIndex === itemIdx));
    } else {
      updatedTable.sections[sectionIdx].items[itemIdx].active =
        !updatedTable.sections[sectionIdx].items[itemIdx].active;
    }
    setTableConfigData(updatedTable);
  };

  const searchHandler = e => {
    const keyword = e.target.value.trim();
    const result = defaultEstimatedViews?.filter(view => view.name.toLowerCase().includes(keyword.toLowerCase()));
    setViewTypes(result);
  };

  const handleMouseEnter = (e, id) => {
    if (e.target) {
      setTooltipId(`item-name-${id}`);
      setItemTooltipValue(e.target.innerText);
      setItemNameDropdown(e.target.scrollWidth > e.target.clientWidth);
    }
  };

  const handleMouseLeave = () => {
    setTooltipId();
    setItemTooltipValue();
    setItemNameDropdown();
  };

  const switchHandler = ({ value: viewType }) => {
    let updatedTable = { ...tableConfigData };
    updateTableConfig({ estimated_time: viewType }, "estimated", sectionIdx, 0);
  };

  useEffect(() => {
    if (!readyToUpdate) {
      setReadyToUpdate(false);
      return;
    }
    let updatedTable = { ...tableConfigData };
    updatedTable.sections[sectionIdx].items = dragItems;
    setTableConfigData(updatedTable);
    setReadyToUpdate(false);
  }, [dragItems]);

  useEffect(() => {
    if (sectionInfo?.items) setSectionItems(sectionInfo.items);
  }, [sectionInfo?.items]);

  // Below code to set item details width
  useLayoutEffect(() => {
    const updateWidth = () => {
      setInsertWidth(false);
      setTimeout(() => {
        if (sectionRef.current) {
          let currScrollWidth = sectionRef.current.scrollWidth;
          if (sectionRef?.current?.getBoundingClientRect()?.width > currScrollWidth) {
            currScrollWidth = sectionRef.current.getBoundingClientRect().width;
          }

          setSectionActualWidth(currScrollWidth);
          setInsertWidth(true);
        }
      }, 100);
    };

    updateWidth(); // Initial measurement

    // Optional: Re-measure on window resize (if needed)
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, [modalFullScreen]);

  useEffect(() => {
    setViewTypes(defaultEstimatedViews);
  }, [dropdownOpen]);

  return (
    <Card className={cx(style["mb-3"])} key={sectionIdx}>
      <CardHeader className={cx(style["d-flex"], style["justify-content-between"], style["align-items-center"])}>
        <div className={cx(style["d-flex"], style["align-items-center"], style["ps-2"])}>
          <div className={style["section-name-space"]}>
            {(tableConfigData?.optionalSection || sectionInfo?.optionalSection) && (
              <>
                {(tableConfigData?.sectionSelectionType === "multiple" || sectionInfo?.optionalSection) && (
                  <div className={cx(style["custom-control"], style["custom-checkbox"], style["form-check"])}>
                    <Input
                      cssModule={style}
                      returnType="noGroup"
                      type="checkbox"
                      id={`multipleOpction${sectionIdx}`}
                      checked={sectionInfo?.active}
                      className={style["form-check-input"]}
                      onChange={() => updateSelectionType("multiple")}
                    />
                  </div>
                )}
                {tableConfigData?.sectionSelectionType === "single" && !sectionInfo?.optionalSection && (
                  <Input
                    type="radio"
                    id={`singleOpction${sectionIdx}`}
                    name="sectional-selection"
                    radioClass="radio-icon mb-0"
                    checked={sectionInfo?.active}
                    className={style["form-check-input"]}
                    onChange={() => updateSelectionType("single")}
                  />
                )}
              </>
            )}
          </div>
          <h6 className={cx(style["mb-0"], style["me-5"], style["fw-7"], style["edit-title"], style["d-flex"])}>
            <div
              id={`section-Name-${sectionIdx}`}
              contentEditable={isEditable}
              onBlur={e => handleBlur(e, sectionIdx)}
              onInput={e => changeSectionName(e)}
              onKeyDown={e => onKeyDownFn(e)}
              suppressContentEditableWarning={true}
              className={cx({ [style["rename"]]: isEditable })}>
              {sectionInfo.name}
            </div>
            {!isEditable && (
              <a onClick={() => handleEditClick(sectionIdx)}>
                <Icon icon="ui-edit" />
              </a>
            )}
          </h6>
        </div>
        {tableConfigData["sections"][0].estimated_time === "estimated_hours" && (
          <ul className={cx(style["d-flex"], style["align-items-center"], style["right"])}>
            <li>
              <h6 className={cx(style["mb-0"], style["fw-7"])}>Total</h6>
            </li>
            <li className={cx(style["ms-3"])}>
              <h6 className={cx(style["mb-0"], style["fw-7"])}>
                <div>{totalEstimatedHours} Hours</div>
              </h6>
            </li>
          </ul>
        )}
      </CardHeader>
      <CardBody
        className={cx(style["card-space"])}>
        <div ref={sectionRef}>
          <div className={cx(style["item-label"])} style={insertWidth ? { width: `${sectionActualWidth}px` } : {}}>
            <ul className={cx(style["d-flex"], style["align-items-center"])}>
              <li>&nbsp;</li>
              {tableConfigData.columns?.map((item, idx) => {
                return (
                  <>
                    {item.slug === "task_name" && (
                      <li key={idx} className={cx(item.className, style["flex-grow-1"])}>
                        {item.name}
                      </li>
                    )}

                    {item.slug === "estimated" && (
                      <>
                        <li key={idx} className={cx(style["estimated-label"], style["justify-content-end"], {[style["ongoing-item"]] : selectedViewType === "Ongoing"})}>
                          <UncontrolledDropdown isOpen={dropdownOpen} toggle={toggle}>
                            <DropdownToggle caret tag="a">
                              <span className={cx(style["fw-7"], style["m-0"])}>{selectedViewType}</span>
                              <Icon icon="ui-arrow-down" additionalclass="ms-2" />
                            </DropdownToggle>
                            <DropdownMenu className={cx(style["shadow"], style["border-0"], style["rounded-sm"])}>
                              <div className={cx(style["search"], style["mb-2"])}>
                                <Input
                                  cssModule={style}
                                  returnType="inputGroup"
                                  addonType="prepend"
                                  type="search"
                                  onChange={e => searchHandler(e)}
                                  autoFocus={true}
                                />
                              </div>

                              {viewTypes?.length === 0 && (
                                <div
                                  className={cx(
                                    style["px-3"],
                                    style["text-muted"],
                                    style["font-15"],
                                    style["no-found"],
                                    style["py-1"]
                                  )}>
                                  No results found
                                </div>
                              )}

                              {viewTypes?.length > 0 &&
                                viewTypes.map((view, i) => (
                                  <>
                                    {view.value !== "ongoing" && (
                                      <DropdownItem tag={"a"}
                                        key={i}
                                        toggle={view.value !== tableConfigData["sections"][0].estimated_time}
                                        className={cx({
                                          [style["current-item"]]:
                                            view.value === tableConfigData["sections"][0].estimated_time,
                                        })}
                                        onClick={() => switchHandler(view)}>
                                        {view.name}
                                      </DropdownItem>
                                    )}
                                    {view.value === "ongoing" && (
                                      <DropdownItem tag={"a"}
                                        key={i}
                                        toggle={view.value !== tableConfigData["sections"][0].estimated_time}
                                        className={cx({
                                          [style["current-item"]]:
                                            view.value === tableConfigData["sections"][0].estimated_time,
                                        }, style["d-flex"], style["align-items-center"])}
                                        onClick={() => switchHandler(view)}>
                                        {view.name}
                                        <div className={cx(style["custom-tooltip"], style["ms-2"])}>
                                          <Icon icon={"ui-info"} additionalclass="font-14" />
                                          <div className={cx(style["custom-tooltip-content"], style["text-center"], style["bottom"])}>
                                            <span>Continuous work with <br/> no fixed duration</span>
                                          </div>
                                        </div>
                                        
                                      </DropdownItem>
                                    )}
                                  </>
                                ))}
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </li>

                        <li className={cx(style["mx-2"])}>
                          <Icon icon="ui-info" additionalclass={"font-14"} id={`estimatedTime-TooltipTotal`} />
                          <UncontrolledTooltip placement="bottom" target={`estimatedTime-TooltipTotal`}>
                            Totals are calculated <br></br> only for Hours
                          </UncontrolledTooltip>
                        </li>
                      </>
                    )}
                  </>
                );
              })}
            </ul>
          </div>
          <div {...containerProps}>
            {dragItems.map((itemInfo, itemIdx) => (
              <div
                className={cx(style["item-details"])}
                key={itemIdx}
                {...getItemProps(itemIdx)}
                style={insertWidth ? { width: `${sectionActualWidth}px` } : {}}>
                <div className={cx(style["item-info"])}>
                  {dragItems.length > 1 && <Icon icon="ui-drag2" additionalclass={"drag-icon"} />}
                  <ul className={cx(style["d-flex"], style["mb-2"], style["align-items-start"])}>
                    <li className={cx(style["ms-0"], style["me-n2"])}>
                      {(sectionInfo?.optionalItem || itemInfo?.optionalItem) && (
                        <>
                          {(sectionInfo?.itemSelectionType === "multiple" ||
                            (itemInfo.optionalItem && itemInfo.itemSelectionType === "multiple")) && (
                            <div className={cx(style["custom-control"], style["custom-checkbox"], style["form-check"])}>
                              <Input
                                cssModule={style}
                                returnType="noGroup"
                                type="checkbox"
                                id={`multipleOpction${sectionIdx}${itemIdx}`}
                                checked={itemInfo?.active}
                                className={style["form-check-input"]}
                                onChange={() => updateItemSelectionType("multiple", itemIdx)}
                              />
                            </div>
                          )}
                          {sectionInfo?.itemSelectionType === "single" && (
                            <Input
                              type="radio"
                              id={`singleOpction${sectionIdx}${itemIdx}`}
                              radioClass="radio-icon mb-0"
                              checked={itemInfo?.active}
                              className={style["form-check-input"]}
                              onChange={() => updateItemSelectionType("single", itemIdx)}
                            />
                          )}
                        </>
                      )}
                    </li>
                    {tableConfigData.columns?.map((item, idx) => {
                      return (
                        <>
                          {item.slug === "task_name" && (
                            <>
                              <li
                                key={idx}
                                className={cx(
                                  item?.inputFieldClass,
                                  style["d-flex"],
                                  style["align-items-center"],
                                  style["flex-grow-1"]
                                )}>
                                <ItemFields
                                  // onMouseDown={e => e.stopPropagation()}
                                  fieldType="text"
                                  itemValue={itemInfo?.data?.[item.slug]}
                                  slug={item.slug}
                                  sectionIdx={sectionIdx}
                                  itemIdx={itemIdx}
                                  updateSectionValue={updateSectionValue}
                                  maxLength={60}
                                  required={true}
                                />
                              </li>
                            </>
                          )}
                          {item.slug === "estimated" && (
                            <li
                              key={idx}
                              className={cx(item?.inputFieldClass, { [style["disable"]]: !itemInfo?.active })}>
                              <ItemFields
                                // onMouseDown={e => e.stopPropagation()}
                                selectedViewType={selectedViewType}
                                fieldType={selectedViewType === "Ongoing" ? "text" : "number"}
                                itemValue={itemInfo?.data?.[item.slug]}
                                slug={item.slug}
                                sectionIdx={sectionIdx}
                                itemIdx={itemIdx}
                                updateSectionValue={updateSectionValue}
                                maxLength={60}
                                required={true}
                                updateTableConfig={updateTableConfig}
                                setEstimatedInputBlur={setEstimatedInputBlur}
                                estimatedInputBlur={estimatedInputBlur}
                                isEnable={selectedViewType === "Ongoing" ? false : true}
                              />
                            </li>
                          )}
                        </>
                      );
                    })}

                    <li className={cx(style["more-action"])}>
                      <ItemDropdown
                        tableConfigData={tableConfigData}
                        updateTableConfig={updateTableConfig}
                        sectionIdx={sectionIdx}
                        itemIdx={itemIdx}
                        itemInfo={itemInfo}
                        deleteSectionItem={deleteSectionItem}
                        updateSectionValue={updateSectionValue}
                        dropdownContainer={dropdownContainer}
                        customDropdownMenuClass={"more-option-dropdownmenu"}
                      />
                    </li>
                  </ul>
                </div>
                <div className={cx(style["item-description"], style["ms-2"], style["d-block"])}>
                  <div className="form-group">
                    <ItemFields
                      fieldType={"textarea"}
                      itemValue={itemInfo?.data?.description}
                      slug={"description"}
                      sectionIdx={sectionIdx}
                      addSectionItem={addSectionItem}
                      itemIdx={itemIdx}
                      updateSectionValue={updateSectionValue}
                      placeholder="Add a description to your task"
                      maxLength={600}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={cx(style["add-item"])} style={{marginLeft : "34px"}}>
          <div className={cx(style["d-flex"], style["align-items-center"])}>
            <a
              className={cx(style["d-flex"], style["align-items-center"], style["color-33"])}
              tabIndex={0}
              data-role="add-task"
              onClick={() => addSectionItem("item", sectionIdx, selectedViewType)}>
              <Icon icon="ui-plus" additionalclass={"me-2"} />
              <span>Task</span>
            </a>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

const ScopeOfWork = ({ closeModal, modalFullScreen, setModalFullScreen, editTable }) => {
  const defaultEstimatedViews = [
    {
      name: "Estimated Hours",
      value: "estimated_hours",
    },
    {
      name: "Estimated Days",
      value: "estimated_days",
    },
    {
      name: "Estimated Weeks",
      value: "estimated_weeks",
    },
    {
      name: "Estimated Months",
      value: "estimated_months",
    },
    {
      name: "Ongoing",
      value: "ongoing",
    },
  ];
  let { metadata, widgets, updateWidgets } = useContext(EditorContext);
  const dropdownContainer = document.querySelector(".scope-of-work-modal");
  const tableDefaultData = JSON.parse(JSON.stringify(widgetConfig?.scope_of_work));

  const {
    state: tableConfigData,
    updateState: setTableConfigData,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo(editTable ? editTable : tableDefaultData?.tableConfig);
  const stateRef = useRef("estimated_hours");

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [insertHtmlMode, setInsertHtmlMode] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [insertedHtmlData, setInsertedHtmlData] = useState([]);
  const [addWidgetClicked, setAddWidgetClicked] = useState(false);
  const [dataparam, setDataParam] = useState();
  const [assetInnerContent, setAssetInnerContent] = useState();
  const [widgetWidth, setWidgetWidth] = useState();
  const [widgetHeight, setWidgetHeight] = useState();
  const [customCss, setCustomCss] = useState(false);
  const [disableInsert, setDisableInsert] = useState(true);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  const [viewTypes, setViewTypes] = useState(defaultEstimatedViews);
  const [totalEstimatedHours, setTotalEstimatedHours] = useState();
  const [estimatedInputBlur, setEstimatedInputBlur] = useState(false)
  const htmlMode = false;

  const { postion: getPosition } = useAlignment(addWidgetClicked, widgetWidth, widgetHeight, "middle-center");
  const getNewWidgetObject = useAddWidget(
    addWidgetClicked,
    SCOPE_OF_WORK,
    dataparam,
    getPosition,
    widgetWidth,
    widgetHeight
  );
  useElementInnerHtml(
    addWidgetClicked,
    setAddWidgetClicked,
    SCOPE_OF_WORK,
    assetInnerContent,
    dataparam,
    getNewWidgetObject,
    customCss
  );

  const setFullScreen = () => {
    setModalFullScreen(!modalFullScreen);
  };

  const close = () => {
    closeModal && closeModal();
    if (modalFullScreen) setModalFullScreen(false);
  };

  const getModalHeight = () => {
    if (window.innerWidth < 480) return "calc(95vh - 3rem)";
    if (window.innerWidth < 768) return "calc(90vh - 5rem)";
    return "calc(84vh - 10rem)";
  };

  // Update Table config data
  const updateTableConfig = (keyValue, type = null, sectionIdx = null, itemIdx = null) => {
    let updatedTable = { ...tableConfigData };
    const keyName = Object.keys(keyValue)[0];
    const keyData = keyValue[keyName];
    if (type === "sections") {
      updatedTable.sections[sectionIdx] = { ...updatedTable.sections[sectionIdx], ...keyValue };
    } else if (type === "items") {
      updatedTable.sections[sectionIdx].items[itemIdx] = {
        ...updatedTable.sections[sectionIdx].items[itemIdx],
        ...keyValue,
      };
    } else if (type === "estimated") {
      const estimatedShow = defaultEstimatedViews.filter(item => item.value === keyValue["estimated_time"])[0]?.name;
      updatedTable.sections[0] = { ...updatedTable.sections[0], ...keyValue };
      updatedTable.sections[0].estimated_show = estimatedShow;
      if (stateRef.current !== keyValue["estimated_time"]) {
        updatedTable.sections[0].items.forEach((obj, index) => {
          updatedTable.sections[0].items[index].data.estimated = "0";
          if (estimatedShow === "Ongoing") updatedTable.sections[0].items[index].data.estimated = "-";
          else updatedTable.sections[0].items[index].data.estimated = "0";
        });
      }
      stateRef.current = keyValue["estimated_time"];
    } else if (type === "estimatedValue") {
      updatedTable.sections[sectionIdx].items[itemIdx].data = {
        ...updatedTable.sections[sectionIdx].items[itemIdx].data,
        ...keyValue,
      };
    } else {
      updatedTable = { ...updatedTable, ...keyValue };
    }

    // Update SectionSelectionType
    if (["optionalSection", "sectionSelectionType"].includes(keyName)) {
      let updateSectionSelectionType = "";

      if (keyName === "optionalSection") {
        if (keyData && sectionIdx === null) {
          updateSectionSelectionType = "single";
          updatedTable.sectionSelectionType = "single";
        } else if (sectionIdx) {
          updateSectionSelectionType = "multiple";
          updatedTable.sections[sectionIdx].sectionSelectionType = "multiple";
        }
      }

      if (keyData === "single" || updateSectionSelectionType === "single") {
        // For "single" type, set first item to active: true and others to active: false
        updatedTable.sections.forEach((section, sectionIndex) => (section.active = sectionIndex === 0));
      } else if (keyData === "multiple" || updateSectionSelectionType === "multiple") {
        // For "multiple" type, set all items to active: true
        updatedTable.sections.forEach(section => (section.active = true));
      }
    }

    if (["itemSelectionType", "optionalItem"].includes(keyName)) {
      let updateSectionSelectionType = "";

      if (keyName === "optionalItem") {
        if (keyData && itemIdx === null) {
          updateSectionSelectionType = "single";
          updatedTable.sections[sectionIdx].itemSelectionType = "single";
        } else {
          updateSectionSelectionType = "multiple";
          updatedTable.sections[sectionIdx].itemSelectionType = "multiple";

          if (typeof itemIdx === "number") {
            updatedTable.sections[sectionIdx].items[itemIdx].itemSelectionType = "multiple";
          }
        }
      }

      if (keyData === "single" || updateSectionSelectionType === "single") {
        updatedTable.sections[sectionIdx].items.forEach((item, itemIndex) => {
          item.active = itemIndex === 0;
          item.itemSelectionType = "single";
        });
      } else if (keyData === "multiple" || updateSectionSelectionType === "multiple") {
        updatedTable.sections[sectionIdx].items.forEach(item => {
          item.active = true;
          item.itemSelectionType = "multiple";
        });
      }
    }
    setTableConfigData(updatedTable);
  };

 const addSectionItem = (type, sectionIdx, selectedViewType) => {
   let updatedTable = { ...tableConfigData };
   if (type === "item" && selectedViewType !== "Ongoing") {
     updatedTable.sections[sectionIdx].items.push({
       active:
         updatedTable?.sections[sectionIdx].optionalItem &&
         updatedTable?.sections[sectionIdx]?.itemSelectionType == "single"
           ? false
           : true,
       optionalItem: false,
       data: {
         id: "",
         task_name: "",
         description: "",
         estimated: "",
       },
     });
   } else {
     updatedTable.sections[sectionIdx].items.push({
       active:
         updatedTable?.sections[sectionIdx].optionalItem &&
         updatedTable?.sections[sectionIdx]?.itemSelectionType == "single"
           ? false
           : true,
       optionalItem: false,
       data: {
         id: "",
         task_name: "",
         description: "",
         estimated: "-",
       },
     });
   }
   setTableConfigData(updatedTable);
 };

  const deleteSectionItem = (type, sectionIdx, itemIdx) => {
    // Addt item/section
    let updatedTable = { ...tableConfigData };
    if (type === "section") {
      updatedTable.sections.splice(sectionIdx, 1);
    } else {
      updatedTable.sections[sectionIdx].items.splice(itemIdx, 1);
    }
    // Update table config data
    setTableConfigData(updatedTable);
  };

  const moveSection = (sectionIdx, position, sectionsArray) => {
    // Check if the section can be moved up or down
    if (
      (position === "up" && sectionIdx === 0) || // Cannot move the first section up
      (position === "down" && sectionIdx === tableConfigData.sections.length - 1) // Cannot move the last section down
    ) {
      return; // Exit the function if the move is invalid
    }
    let updatedTable = { ...tableConfigData };

    // Calculate the new index based on the position
    const newIndex = position === "up" ? sectionIdx - 1 : sectionIdx + 1;

    // Swap the sections in the array
    [updatedTable.sections[sectionIdx], updatedTable.sections[newIndex]] = [
      updatedTable.sections[newIndex],
      updatedTable.sections[sectionIdx],
    ];
    setTableConfigData(updatedTable);
  };

  const updateSectionValue = (updates, sectionIdx, itemIdx) => {
    if (typeof sectionIdx !== "number" || typeof itemIdx !== "number") return;

    let updatedTable = { ...tableConfigData };
    const section = updatedTable.sections[sectionIdx];
    const item = section.items[itemIdx];

    Object.entries(updates).forEach(([slug, value]) => {
        if (item.data[slug] !== value) {
          item.data[slug] = value;
        }
    });

    setTableConfigData(updatedTable);
  };

  const handleEditClick = sectionIdx => {
    setIsEditable(!isEditable);

    // Focus and place cursor at end after state update
    setTimeout(() => {
      const editableDiv = document.getElementById(`section-Name-${sectionIdx}`);
      if (editableDiv) {
        editableDiv.focus();
        return;
        // Move cursor to end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(editableDiv);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  };

  const changeSectionName = e => {
    const maxLength = 40; // Set your desired max length
    if (e.currentTarget.textContent.length > maxLength) {
      e.currentTarget.textContent = e.currentTarget.textContent.substring(0, maxLength);
    }
  };

  const onKeyDownFn = e => {
    const maxLength = 40;
    const currentText = e.currentTarget.textContent;

    // Allow Ctrl+A (Select All), deletions, and navigation keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];

    // Allow Ctrl+A (keyCode 65 with Ctrl pressed)
    const isCtrlA = (e.ctrlKey || e.metaKey) && e.key === "a";

    // Block input if maxLength reached (unless allowed key)
    if (currentText.length >= maxLength && !allowedKeys.includes(e.key) && !isCtrlA) {
      e.preventDefault();
    }

    // Prevent Enter key (optional)
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur(); // Optionally blur on Enter
    }
  };

  const handleBlur = (e, sectionIdx) => {
    let updatedTable = { ...tableConfigData };
    setIsEditable(!isEditable);

    if (e.target.textContent.length === 0) {
      e.target.textContent = updatedTable.sections[sectionIdx].name; // Revert to previous value
      return;
    }

    updatedTable.sections[sectionIdx].name = e.target.textContent;
    setTableConfigData(updatedTable);
  };

  const insertTable = () => {
    let insertedTableData = { ...tableConfigData };
    setInsertedHtmlData({ ...insertedTableData });
    if (htmlMode) setInsertHtmlMode(true);

    // Use this htmlString for insert
    if (!editTable) {
      const htmlString = ReactDOMServer.renderToString(
        <CanvasHtmlData canvasConfigData={insertedTableData} />
      );
      const elem = document.createElement("div");
      elem.innerHTML = htmlString;

      // add pheudo elem in editor
      const parent = document.getElementById(metadata.activeBlockId);
      const pseudoElem = document.createElement("div");
      pseudoElem.id = "pseudoElem";
      pseudoElem.style.width = "700px";
      pseudoElem.innerHTML = htmlString;
      parent.appendChild(pseudoElem);

      // insert into editor
      setTimeout(() => {
        const jsonString = JSON.stringify(insertedTableData);
        let paramObj = { "data-table-config": jsonString };
        let dataAttr = { ...widgetConfig[SCOPE_OF_WORK].dataAttr, ...paramObj };
        let offsetHeight = document.querySelector("#pseudoElem .scopeOfWork_editor")?.offsetHeight;
        let offsetWidth = 700;
        let scale = 1242 / offsetWidth; // 1242 is page width(which is static)
        document.querySelector("#pseudoElem").remove();

        setCustomCss({
          transform: `scale(${scale})`,
          width: `${offsetWidth}px`,
          height: `${offsetHeight}px`,
        }); // set width height to inner div
        setWidgetWidth(offsetWidth * scale); // set width  to main div
        setWidgetHeight(offsetHeight * scale); // set height to main div
        setAssetInnerContent(elem);
        setDataParam(dataAttr);
        setAddWidgetClicked(true);
      }, 50);
    } else {
      const jsonString = JSON.stringify(insertedTableData);
      const htmlString = ReactDOMServer.renderToString(
        <CanvasHtmlData canvasConfigData={insertedTableData} />
      );
      const elem = document.createElement("div");
      elem.innerHTML = htmlString;
      document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).innerHTML = elem.innerHTML;

      // add pheudo elem in editor
      const parent = document.getElementById(metadata.activeBlockId);
      const pseudoElem = document.createElement("div");
      pseudoElem.id = "pseudoElem";
      pseudoElem.style.width = document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width;
      pseudoElem.innerHTML = htmlString;
      parent.appendChild(pseudoElem);

      setTimeout(() => {
        let offsetHeight = document.querySelector("#pseudoElem .scopeOfWork_editor")?.offsetHeight;
        let offsetWidth = parseFloat(
          document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width
        );
        let scale = parseFloat(
          document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.transform.split("(")[1]
        );
        document.querySelector("#pseudoElem").remove();
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.width = `${offsetWidth}px`;
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.height = `${offsetHeight}px`;
        document.querySelector(`#${metadata.activeWidgetId[0]} .dhp-widget-inner`).style.transform = `scale(${scale})`;

        const targetWidgetIndex = widgets?.findIndex(widget => widget.id === metadata.activeWidgetId[0]);

        let newArray = Object.assign([...widgets]);
        newArray = Object.assign([...newArray], {
          [targetWidgetIndex]: {
            ...newArray[targetWidgetIndex],
            data: {
              ...newArray[targetWidgetIndex].data,
              "data-table-config": jsonString,
            },
            style: {
              ...newArray[targetWidgetIndex].style,
              width: `${offsetWidth * scale}px`,
              height: `${offsetHeight * scale}px`,
            },
            innerHTML: document.getElementById(metadata.activeWidgetId[0]).innerHTML,
          },
        });
        updateWidgets(newArray);
      }, 50);

      setTimeout(() => {
        close();
      }, 100);
    }
  };

  const doUndo = () => {
    setIsUndoRedo(true);
    undo();
  };

  const doRedo = () => {
    setIsUndoRedo(true);
    redo();
  };

  const calculateTotalTime = () => {
    let updatedTable = { ...tableConfigData };
    let minHour = 0;

    tableConfigData.sections?.forEach(section => {
      section.items?.forEach(val => {
        if (val.active && parseFloat(val.data.estimated)) {
          minHour += parseFloat(val.data.estimated);
        }
      });
    });

    // Round to 1 decimal places
    const total = Number(minHour.toFixed(1));

    setTotalEstimatedHours(total);
    updatedTable.sections[0].total = total;
    return total;
  };

  useEffect(() =>{
    calculateTotalTime();
  }, [estimatedInputBlur, tableConfigData])

  useEffect(() => {
    const hasEmptyItemName = tableConfigData.sections.some(section => {
      return section.items.some(item => {
        const { task_name, estimated } = item.data;
        const estValue = estimated?.toString().trim();

        if (section.estimated_time === "ongoing") return task_name.trim() === "";
        else return task_name.trim() === "" || estValue === "";
      });
    });

    setDisableInsert(hasEmptyItemName);
  }, [tableConfigData]);

  // Close pricing table modal after innserted to canvas
  useEffect(() => {
    if (getNewWidgetObject?.id && !htmlMode) close();
  }, [getNewWidgetObject]);

  useEffect(() => {
    // Update table config data
    if (editTable) stateRef.current = editTable?.["sections"]?.[0]?.estimated_time;
    setTableConfigData(tableConfigData);
  }, []);

  return (
    <>
      {!insertHtmlMode && (
        <div className="table-modal-wrap">
          <div className="d-flex justify-content-between modal-header">
            <h4 className={cx(style["fw-7"], style["mb-0"])}>Scope of Work</h4>
            <ul className={style["d-flex"]}>
              <li
                className={cx(style["custom-tooltip"], { [style["disabled"]]: !canUndo })}
                onClick={doUndo}
                disabled={!canUndo}>
                <span className={cx(style["cursor-pointer"], style["d-flex"], style["undo"])}>
                  <Icon icon="ui-undo" />
                </span>
                <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Undo</div>
              </li>
              <li
                className={cx(style["custom-tooltip"], { [style["disabled"]]: !canRedo })}
                onClick={doRedo}
                disabled={!canRedo}>
                <span className={cx(style["cursor-pointer"], style["d-flex"], style["redo"])}>
                  <Icon icon="ui-redo" />
                </span>
                <div className={cx(style["custom-tooltip-content"], style["bottom"])}>Redo</div>
              </li>
              <li className={cx(style["expand-btn"], style["rounded"])} onClick={setFullScreen}>
                <Icon icon={modalFullScreen ? "ui-collapse" : "ui-expand"} />
              </li>
              <li className={cx(style["cross-modal"], style["rounded"])} onClick={close}>
                <Icon icon="ui-close" />
              </li>
            </ul>
          </div>

          <div
            className={cx(style["modal-body"], style["customScroll"], style["scroll-Y"])}
            style={{ height: getModalHeight(), maxHeight: "calc(84% - 2rem)" }}>
            {tableConfigData?.sections?.map((sectionInfo, sectionIdx) => (
              <SectionComponent
                key={sectionIdx}
                sectionInfo={sectionInfo}
                sectionIdx={sectionIdx}
                tableConfigData={tableConfigData}
                handleBlur={handleBlur}
                isEditable={isEditable}
                handleEditClick={handleEditClick}
                changeSectionName={changeSectionName}
                onKeyDownFn={onKeyDownFn}
                moveSection={moveSection}
                updateTableConfig={updateTableConfig}
                deleteSectionItem={deleteSectionItem}
                updateSectionValue={updateSectionValue}
                addSectionItem={addSectionItem}
                setTableConfigData={setTableConfigData}
                dropdownContainer={dropdownContainer}
                modalFullScreen={modalFullScreen}
                dropdownOpen={dropdownOpen}
                setDropdownOpen={setDropdownOpen}
                defaultEstimatedViews={defaultEstimatedViews}
                viewTypes={viewTypes}
                setViewTypes={setViewTypes}
                setEstimatedInputBlur={setEstimatedInputBlur}
                estimatedInputBlur={estimatedInputBlur}
                totalEstimatedHours={totalEstimatedHours}
              />
            ))}

            <Row className={style["justify-content-between"]}>
              <Col></Col>
              <Col xs="auto"></Col>
            </Row>
          </div>

          <div className={style["modal-footer"]}>
            <button
              type="button"
              onClick={insertTable}
              className={cx(style["btn"], style["btn-primary"])}
              disabled={disableInsert}>
              {!editTable ? "Insert" : "Update"}
            </button>
          </div>
        </div>
      )}

      {/* below section need to delete after insert html page design done */}
      {insertHtmlMode && (
        <Modal
          size="lg"
          showModal={insertHtmlMode}
          component={CanvasHtmlData}
          canvasConfigData={insertedHtmlData}
          setShowModal={setInsertHtmlMode}
        />
      )}
    </>
  );
};

export default ScopeOfWork;
